import { Component } from '@opten/compiler/metadata/component';
import { replaceAntiForgeryToken } from '../../functions/add-antiforgery-token';

@Component({ className: 'js-vehicles-storage-preview-wrapper', observe: true })
export class VehiclesStoragePreviewComponent {
    previewContainers: HTMLElement[];
    private resizeTimeout: number | null = null;
    language: string;

    constructor(private element: HTMLElement) {
        this.init();
    }

    private init() {
        this.initPreviewContainers();
        
        // Handle window resize with debouncing
        window.addEventListener('resize', () => {
            this.reAlign();
        });
        this.reAlign(100); // Initial alignment after a short delay to ensure all elements are rendered
        var tabContentParent = this.element.closest('.js-tab-content');
        if (tabContentParent) {
            tabContentParent.addEventListener('shown.merbag.tabs', () => {  this.reAlign(100); });
        }
    }
    private reAlign(timeOutDuration: number = 250) {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        this.resizeTimeout = window.setTimeout(() => {
            this.alignElementsInRows();
        }, timeOutDuration);
    }
    private initPreviewContainers() {
        this.previewContainers = Array.from(this.element.querySelectorAll('.js-vehicles-storage-preview')) as HTMLElement[];
        if (!this.previewContainers.length) return;
        var dataLanguage = document.body.getAttribute('data-language-long');
        if (dataLanguage && dataLanguage.length > 0) {
            this.language = dataLanguage;
        }
        this.previewContainers.forEach(container => {
            const leasingContainer = container.getElementsByClassName('js-vehicles-storage-preview-leasing-calculator');
            if (leasingContainer && leasingContainer.length > 0) {
                const leasingForms = container.getElementsByClassName('js-vehicles-storage-preview-leasing-calculator-form');
                if (leasingForms && leasingForms.length > 0) {
                    this.handleSubmitLeasingForm(leasingForms[0] as HTMLFormElement, container, leasingContainer[0] as HTMLElement);
                }
            }

        });
    }

    private handleSubmitLeasingForm(leasingFormElement: HTMLFormElement, previewContainer: HTMLElement, leasingCalculatorElement: HTMLElement) {
        if ($(leasingFormElement).valid()) {
            replaceAntiForgeryToken(() => {
                var formData = $(leasingFormElement).serialize();

                fetch(leasingFormElement.action, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'accept-language': this.language,
                        // todo: why? if we change to a api controller we don't have this problem...
                        'X-Requested-With': 'XMLHttpRequest' // otherwise accept-language is ignored,
                    },
                    body: formData,
                    credentials: 'include'
                })
                    .then(response => {
                        const contentType = response.headers.get('content-type');
                        if (contentType && (contentType.includes('text/html') || contentType.includes('text/plain'))) {
                            return response.text();
                        }
                    })
                    .then(data => {
                        this.handleDataLeasingCalculatorResult(data, previewContainer, leasingCalculatorElement)
                    });
            });
        }
    }
    private handleDataLeasingCalculatorResult(data: string, previewContainer: HTMLElement, leasingCalculatorElement: HTMLElement) {
        if (data != null && data.length > 0) {
            leasingCalculatorElement.innerHTML = data;
            previewContainer.classList.add('has--monthly-leasing');
            this.reAlign(100);
        }
        else{
            if(leasingCalculatorElement.parentNode){
                leasingCalculatorElement.parentNode.removeChild(leasingCalculatorElement);
            }
            this.reAlign(100);
        }
    }

    private alignElementsInRows() {

        if (!this.previewContainers.length) return;

        // Group elements by their visual row once
        const rows = this.groupElementsByRow(this.previewContainers);

        // Apply alignment using the same row grouping
        this.alignTitlesInRows(rows);
        this.alignPricesInRows(rows);
    }

    private alignTitlesInRows(rows: HTMLElement[][]) {
        // Reset all title heights first
        this.previewContainers.forEach(container => {
            const titleElement = container.querySelector('.js-vehicles-storage-preview-title') as HTMLElement;
            if (titleElement) {
                titleElement.style.removeProperty('--title-height');
            }
        });

        // For each row, find the tallest title and apply that height to all titles in the row
        rows.forEach(row => {
            if (row.length <= 1) return; // Skip if only one element in row

            // Find the maximum height in this row
            let maxHeight = 0;
            row.forEach(container => {
                const titleElement = container.querySelector('.js-vehicles-storage-preview-title') as HTMLElement;
                if (titleElement) {
                    const height = titleElement.offsetHeight;
                    if (height > maxHeight) {
                        maxHeight = height;
                    }
                }
            });

            // Apply the maximum height to all title elements in the row
            row.forEach(container => {
                const titleElement = container.querySelector('.js-vehicles-storage-preview-title') as HTMLElement;
                if (titleElement) {
                    titleElement.style.setProperty('--title-height', `${maxHeight}px`);
                }
            });
        });
    }

    private alignPricesInRows(rows: HTMLElement[][]) {
        // Reset all classes first
        this.previewContainers.forEach(container => {
            container.classList.remove('has--monthly-leasing-in-row');
        });

        // For each row, check if any element has monthly leasing
        rows.forEach(row => {
            if (row.length <= 1) return; // Skip if only one element in row

            const hasMonthlyLeasing = row.some(container =>
                container.classList.contains('has--monthly-leasing')
            );

            // If any element in the row has monthly leasing, add class to all price elements in the row
            if (hasMonthlyLeasing) {
                row.forEach(container => {
                    container.classList.add('has--monthly-leasing-in-row');
                });
            }
        });
    }

    private groupElementsByRow(elements: HTMLElement[]): HTMLElement[][] {
        const rows: HTMLElement[][] = [];
        let currentRow: HTMLElement[] = [];
        let lastTop = -1;
        const tolerance = 10; // Pixels tolerance for same row detection

        elements.forEach((container: HTMLElement) => {
            const rect = container.getBoundingClientRect();
            const currentTop = Math.round(rect.top);

            // If this element is on a new row (significant vertical difference)
            if (lastTop !== -1 && Math.abs(currentTop - lastTop) > tolerance) {
                if (currentRow.length > 0) {
                    rows.push([...currentRow]);
                }
                currentRow = [container];
            } else {
                currentRow.push(container);
            }

            lastTop = currentTop;
        });

        // Add the last row
        if (currentRow.length > 0) {
            rows.push(currentRow);
        }

        return rows;
    }
}